import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import NumberFormat from 'react-number-format';
import { InfoTooltip } from 'components';
import {
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { changeMarketingData } from 'redux/actions/subscription';
import { calculateAddonsMonthlyPrice, calculateAddonsOneTypePrice } from 'helpers/priceCalculations';


const useStyles = makeStyles(() => ({
  textColorGray: {
    color: '#4F566B'
  }
}));

const ListBullet = withStyles({
  root: {
    padding: '3px 0',

    '& .MuiListItem-root': {
      position: 'relative',
      padding: '3px 5px 3px 16px'
    },

    '& .MuiListItem-root::before': {
      content: '" "',
      display: 'inline-block',
      position: 'absolute',
      left: '6px',
      width: '4px',
      height: '4px',
      backgroundColor: '#3C4257',
      borderRadius: '50%',
      marginRight: '10px'
    }
  }
})(List);

const ListFeatures = withStyles({
  root: {
    padding: '3px 0',

    '& .MuiListItem-root': {
      padding: '3px 5px 3px 5px'
    }
  }
})(List);

const CheckIcon = withStyles({
  root: {
    minWidth: '28px',
    color: '#219653'
  }
})(ListItemIcon);

export default () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    userProfile: {marketing},
    additionalSkus,
    additionalSpecials,
    additionalFeatures,
  } = useSelector((state: IReduxState) => state.subscription);
  const {featureIds, skuIds, specialIds} = marketing;


  const selectedSku = additionalSkus.find((opt) => opt._id === skuIds[0]);
  const {
    value: skusCount,
    price: skusPrice,
    isOneTime: skusIsOneTime
  } = selectedSku || {};

  const selectedSpecial = additionalSpecials.find((opt) => opt._id === specialIds[0]);
  const {
    value: specialsCount,
    price: specialsPrice,
    isOneTime: specialsIsOneTime
  } = selectedSpecial || {};

  const isMonthlyFeaturesHavePrice = featureIds
    .filter((featureId) => {
      const {priceMonthly} = additionalFeatures.find((opt) => opt._id === featureId) || {};
      return priceMonthly != 0;
    });

  const isOneTimeFeaturesHavePrice = featureIds
    .filter((featureId) => {
      const {priceOneTime} = additionalFeatures.find((opt) => opt._id === featureId) || {};
      return priceOneTime != 0;
    });

  const totalOneTimePrice = calculateAddonsOneTypePrice({ featureIds, additionalFeatures });
  const totalSubscriptionPrice = calculateAddonsMonthlyPrice({ featureIds, additionalFeatures, skusPrice, skusIsOneTime, specialsPrice, specialsIsOneTime });

  const handleSelectFeature = (event) => {
    const {checked, value} = event.target;

    if (checked) {
      dispatch(changeMarketingData({ ...marketing, featureIds: [...marketing.featureIds, event.target.value]}));
    } else {
      dispatch(changeMarketingData({ ...marketing, featureIds: marketing.featureIds.filter((id) => id !== value)}));
    }
  };

  return (
    <Card elevation={25} className={`my-8 p-8 ${classes.textColorGray}`}>
      <Grid container spacing={3} className="mb-5">
        <Grid item xs={12} md={3}>
          <Typography variant="h4" className="h4">SKU</Typography>
          <Typography variant="body1">
            {skuIds[0] &&
            <>
              {skusIsOneTime ? 'Setup Price' : 'Monthly Price'}
            </>
            }
          </Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          <Card elevation={25} className="p-5">
            <FormControl className="w-full">
              <RadioGroup
                aria-label="skus"
                name="skus"
                value={skuIds[0] || ''}
                onChange={(event) => dispatch(changeMarketingData({ ...marketing, skuIds: [event.target.value]}))}
              >
                <FormControlLabel
                  value={''}
                  control={<Radio color="primary" size="small" />}
                  label="None"
                />
                {additionalSkus.map((option) => (
                  <FormControlLabel
                    key={option._id}
                    value={option._id}
                    control={<Radio color="primary" size="small" />}
                    label={
                      <div className="flex items-center justify-between">
                        <div>
                          {option.value} Additional SKUs (includes Products & Treatments)
                        </div>
                        <div>
                          <NumberFormat
                            value={option.price}
                            displayType="text"
                            thousandSeparator={','}
                            decimalSeparator={'.'}
                            prefix={'+$'}
                            fixedDecimalScale
                            decimalScale={2}
                          />
                        </div>
                      </div>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3} className="mb-5">
        <Grid item xs={12} md={3}>
          <Typography variant="h4" className="h4">Specials</Typography>
          <Typography variant="body1">
            {specialIds[0] &&
            <>
              {specialsIsOneTime ? 'Setup Price' : 'Monthly Price'}
            </>
            }
          </Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          <Card elevation={25} className="p-5">
            <FormControl className="w-full">
              <RadioGroup
                aria-label="specials"
                name="specials"
                value={specialIds[0] || ''}
                onChange={(event) => dispatch(changeMarketingData({ ...marketing, specialIds: [event.target.value]}))}
              >
                <FormControlLabel
                  value={''}
                  control={<Radio color="primary" size="small" />}
                  label="None"
                />
                {additionalSpecials.map((option) => (
                  <FormControlLabel
                    key={option._id}
                    value={option._id}
                    control={<Radio color="primary" size="small" />}
                    label={
                      <div className="flex items-center justify-between">
                        <div>
                          Up to {option.value} Concurrent Specials includes {Number(option.value) * 12} Custom Promotions and access to the Promotion Library.
                        </div>
                        <div>
                          <NumberFormat
                            value={option.price}
                            displayType="text"
                            thousandSeparator={','}
                            decimalSeparator={'.'}
                            prefix={'+$'}
                            fixedDecimalScale
                            decimalScale={2}
                          />
                        </div>
                      </div>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Card>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Typography variant="h4" className="h4">Features/Add-Ons</Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          {additionalFeatures.map((option, index) => (
            <Card elevation={25} className={`p-5 ${additionalFeatures.length !== index + 1 && 'mb-5'}`} key={option._id}>
              <FormControlLabel
                className="items-start w-full"
                control={
                  <Checkbox
                    color="primary"
                    size="small"
                    value={option._id}
                    checked={featureIds.includes(option._id)}
                    onChange={handleSelectFeature}
                  />}
                label={
                  <div className="flex-column mt-2">
                    <div>
                      {option.label}
                      {option.description.length ? <InfoTooltip title={option.description} /> : ''}
                    </div>
                    <ListBullet className="ml-2">
                      <ListItem className="flex items-center justify-between w-full">
                        <span>Setup Price</span>
                        <NumberFormat
                          value={option.priceOneTime}
                          displayType="text"
                          thousandSeparator={','}
                          decimalSeparator={'.'}
                          prefix={'+$'}
                          fixedDecimalScale
                          decimalScale={2}
                        />
                      </ListItem>
                      <ListItem className="flex items-center justify-between w-full">
                        <span>Monthly Price</span>
                        <NumberFormat
                          value={option.priceMonthly}
                          displayType="text"
                          thousandSeparator={','}
                          decimalSeparator={'.'}
                          prefix={'+$'}
                          fixedDecimalScale
                          decimalScale={2}
                        />
                      </ListItem>
                    </ListBullet>
                  </div>
                }
              />
            </Card>
          ))}
        </Grid>
      </Grid>
      <Grid container spacing={3} className="mb-5">
        <Grid item xs={12} md={3} />
        <Grid item xs={12} md={9}>
          <Divider className="my-5" />
          <Card elevation={25} className="bg-light-blue p-5">
            <Typography variant="h4" className="h4 mb-3">Summary</Typography>
            {(!!isOneTimeFeaturesHavePrice.length || skusIsOneTime || specialsIsOneTime) &&
            <>
              <Typography variant="h5" className="h5 mb-0">Features/Add-Ons Setup Fee</Typography>
              <ListFeatures className="ml-2 mb-5">
                {featureIds.map((featureId) => {
                  const feature = additionalFeatures.find((opt) => opt._id === featureId);

                  if (!feature) {
                    return null;
                  }

                  const {label, priceOneTime, _id} = feature;

                  if (priceOneTime == 0) {
                    return null;
                  }

                  return (
                    <ListItem key={_id}>
                      <CheckIcon>
                        <Icon fontSize="small">check</Icon>
                      </CheckIcon>
                      <div className="flex items-center justify-between w-full">
                        <span>{label}</span>
                        <NumberFormat
                          value={priceOneTime}
                          displayType="text"
                          thousandSeparator={','}
                          decimalSeparator={'.'}
                          prefix={'$'}
                          fixedDecimalScale
                          decimalScale={2}
                        />
                      </div>
                    </ListItem>
                  );
                })}
                {(!!skusCount && skusIsOneTime) &&
                <ListItem>
                  <CheckIcon>
                    <Icon fontSize="small">check</Icon>
                  </CheckIcon>
                  <div className="flex items-center justify-between w-full">
                    <span>
                      Additional SKUs:{' '}
                      <strong>Store {skusCount} SKUs</strong>
                    </span>
                    <NumberFormat
                      value={skusPrice}
                      displayType="text"
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      prefix={'$'}
                      fixedDecimalScale
                      decimalScale={2}
                    />
                  </div>
                </ListItem>
                }
                {(!!specialsCount && specialsIsOneTime) &&
                <ListItem>
                  <CheckIcon>
                    <Icon fontSize="small">check</Icon>
                  </CheckIcon>
                  <div className="flex items-center justify-between w-full">
                    <span>
                      Specials:{' '}
                      <strong>{specialsCount} Specials - concurrent specials per month</strong>
                    </span>
                    <NumberFormat
                      value={specialsPrice}
                      displayType="text"
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      prefix={'$'}
                      fixedDecimalScale
                      decimalScale={2}
                    />
                  </div>
                </ListItem>
                }
              </ListFeatures>
            </>
            }
            {(!!isMonthlyFeaturesHavePrice.length || !skusIsOneTime || !specialsIsOneTime) &&
            <>
              <Typography variant="h5" className="h5 mb-0">Features/Add-Ons Monthly Cost</Typography>
              <ListFeatures className="ml-2">
                {featureIds.map((featureId) => {
                  const feature = additionalFeatures.find((opt) => opt._id === featureId);

                  if (!feature) {
                    return null;
                  }

                  const {label, priceMonthly, _id} = feature;

                  if (priceMonthly == 0) {
                    return null;
                  }

                  return (
                    <ListItem key={_id}>
                      <CheckIcon>
                        <Icon fontSize="small">check</Icon>
                      </CheckIcon>
                      <div className="flex items-center justify-between w-full">
                        <span>{label}</span>
                        <NumberFormat
                          value={priceMonthly}
                          displayType="text"
                          thousandSeparator={','}
                          decimalSeparator={'.'}
                          prefix={'$'}
                          fixedDecimalScale
                          decimalScale={2}
                        />
                      </div>
                    </ListItem>
                  );
                })}
                {(!!skusCount && !skusIsOneTime) &&
                <ListItem>
                  <CheckIcon>
                    <Icon fontSize="small">check</Icon>
                  </CheckIcon>
                  <div className="flex items-center justify-between w-full">
                    <span>
                      Additional SKUs:{' '}
                      <strong>Store {skusCount} SKUs</strong>
                    </span>
                    <NumberFormat
                      value={skusPrice}
                      displayType="text"
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      prefix={'$'}
                      fixedDecimalScale
                      decimalScale={2}
                    />
                  </div>
                </ListItem>
                }
                {(!!specialsCount && !specialsIsOneTime) &&
                <ListItem>
                  <CheckIcon>
                    <Icon fontSize="small">check</Icon>
                  </CheckIcon>
                  <div className="flex items-center justify-between w-full">
                    <span>
                      Specials:{' '}
                      <strong>{specialsCount} Specials - concurrent specials per month</strong>
                    </span>
                    <NumberFormat
                      value={specialsPrice}
                      displayType="text"
                      thousandSeparator={','}
                      decimalSeparator={'.'}
                      prefix={'$'}
                      fixedDecimalScale
                      decimalScale={2}
                    />
                  </div>
                </ListItem>
                }
              </ListFeatures>
            </>
            }
            <Divider className="my-5" />
            <div className="w-full flex items-center justify-between mb-5">
              <Typography variant="h5" className="h5 mb-0">Total Setup Price</Typography>
              <div className="font-bold">
                <NumberFormat
                  value={totalOneTimePrice}
                  displayType="text"
                  thousandSeparator={','}
                  decimalSeparator={'.'}
                  prefix={'$'}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </div>
            <div className="w-full flex items-center justify-between">
              <Typography variant="h5" className="h5 mb-0">Total Monthly Subscription</Typography>
              <div className="font-bold">
                <NumberFormat
                  value={totalSubscriptionPrice}
                  displayType="text"
                  thousandSeparator={','}
                  decimalSeparator={'.'}
                  prefix={'$'}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </div>
          </Card>
        </Grid>
      </Grid>
    </Card>
  );
};
